import { StrictMode, createContext, useContext } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../js/api/queryClient";
import { CardActionsMenu } from "./components/collection-save/card-actions-menu";

const CollectionCardMenuContext = createContext();
export const useCollectionCardMenu = () => useContext(CollectionCardMenuContext);

function CollectionCardMenuProvider({ data, children }) {
  return (
    <CollectionCardMenuContext.Provider value={data}>
      {children}
    </CollectionCardMenuContext.Provider>
  );
}

/**
 * Marks a mount point whose root already exists.
 *
 * Without it, re-running the renderer calls `createRoot` a second time on containers that already
 * have one — React logs "You are calling createRoot() on a container that has already been passed
 * to createRoot() before" and builds a second root over the same DOM. `save-color.jsx` re-renders
 * unguarded and does exactly that on every AJAX response; this does not copy that part.
 */
const MOUNTED = "data-collection-card-menu-mounted";

/**
 * One root per card. There are many cards on a listing page, so this is `querySelectorAll` and a
 * root each — the same shape `follow-member.jsx` uses, and the reason the existing vanilla
 * `edit-collection-menu.js` could not be reused (it is `querySelector`, singular, bound to one id).
 *
 * The modals themselves are NOT rendered here. They live in the single `main-react` host, so a page
 * showing twenty cards mounts twenty small menus and one modal, rather than twenty of each.
 *
 * SAFE TO CALL REPEATEDLY. Only unmounted containers are selected, so re-running it after an AJAX
 * response mounts the new cards and leaves the existing ones alone.
 */
function CollectionCardMenuRenderer() {
  const elements = document.querySelectorAll(`.react-collection-card-menu:not([${MOUNTED}])`);

  elements.forEach((element) => {
    let initialProps = {};
    try {
      initialProps = JSON.parse(element.getAttribute("data-initial-props"));
    } catch (error) {
      console.error(error, "Could not parse initial props JSON for CollectionCardMenu");
      return;
    }

    // Flagged before rendering, so a throw inside render cannot leave the container looking
    // unmounted and get it picked up again on the next pass.
    element.setAttribute(MOUNTED, "true");

    createRoot(element).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <CollectionCardMenuProvider data={initialProps}>
            <CardActionsMenu />
          </CollectionCardMenuProvider>
        </QueryClientProvider>
      </StrictMode>
    );
  });
}

export const initCollectionCardMenu = () => {
  if (document.readyState !== "loading") {
    CollectionCardMenuRenderer();
  } else {
    document.addEventListener("DOMContentLoaded", CollectionCardMenuRenderer);
  }

  /*
   * CARDS ARRIVE AFTER PAGE LOAD ON EVERY LISTING SURFACE, and a card whose menu was never
   * mounted has a "…" button that does nothing at all:
   *
   *   - "Load more" on Community Collections appends cards to the existing grid
   *     (js/communityCollections.js)
   *   - filtering, sorting and the back button replace .result-container wholesale
   *     (js/modules/ajaxFormSubmission.js)
   *
   * Both already announce themselves with this event — it is named for save-colour because that
   * was its first consumer, not because it is limited to it — so listening is all this needs.
   *
   * Registered immediately rather than inside DOMContentLoaded: main.jsx is a deferred module, so
   * by the time this runs the document is already "interactive" and a DOMContentLoaded handler
   * would be the only thing standing between a member and a dead button if that ordering ever
   * changed.
   */
  window.emitter?.addListener("rerenderReactSaveColorComponents", CollectionCardMenuRenderer);
};
